import { Route, Routes, useLocation } from 'react-router-dom';
import { Flex, Link, Tabs, TabsContent, TabsList } from '@optiaxiom/react';

import { PageHeader } from '@frontend/components';
import { TabLink } from '@frontend/components/ui';
import { FeatureFlag } from '@frontend/contexts';
import { useNamespacedNavigate } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';
import { SpecializedAgentTab } from '@frontend/types';

import { AgentExecutionsTab } from './executions/AgentExecutionsTab';
import { MarketPlaceTab } from './marketplace/MarketPlaceTab';
import { SpecializedAgentCreationForm } from './specialized-agent/agent-form/SpecializedAgentForm';
import { AgentRouterPage } from './AgentRouterPage';
import { AgentTab } from './AgentTab';
import { WorkFlowAgentForm } from './workflow-agent';

export function AgentPage() {
  const navigate = useNamespacedNavigate();
  const location = useLocation();
  const backHref = new URLSearchParams(location.search).get('backHref');

  const getActiveTab = (): SpecializedAgentTab => {
    if (location.pathname.includes('/agent-directory')) return 'agent-directory';
    if (location.pathname.includes('/create')) return 'agent-form';
    if (location.pathname.includes('/executions')) return 'executions';
    return 'agents';
  };

  const handleTabChange = (value: string) => {
    const tab = value as SpecializedAgentTab;
    if (tab === 'agents') {
      navigate('/agents');
    } else if (tab === 'agent-directory') {
      navigate('/agents/agent-directory');
    } else if (tab === 'executions') {
      navigate('/agents/executions');
    }
  };

  return (
    <Routes>
      <Route path="/by-agent-id/:humanReadableAgentId" element={<AgentRouterPage />} />
      <Route
        path="/specialized/create"
        element={<SpecializedAgentCreationForm backHref={backHref} isStickyHeader />}
      />
      <Route
        path="/specialized/:agentId"
        element={<SpecializedAgentCreationForm backHref={backHref} isStickyHeader />}
      />
      <Route
        path="/specialized/:agentId/copy"
        element={<SpecializedAgentCreationForm backHref={backHref} isStickyHeader />}
      />
      <Route
        path="/workflow/create"
        element={
          <FeatureFlag featureKey="workflow-agent">
            <WorkFlowAgentForm backHref={backHref} />
          </FeatureFlag>
        }
      />
      <Route
        path="/workflow/edit/:id"
        element={
          <FeatureFlag featureKey="workflow-agent">
            <WorkFlowAgentForm backHref={backHref} />
          </FeatureFlag>
        }
      />
      <Route
        path="/workflow/edit/:id/executions/:executionId"
        element={
          <FeatureFlag featureKey="workflow-agent">
            <WorkFlowAgentForm backHref={backHref} />
          </FeatureFlag>
        }
      />
      <Route
        path="/workflow/:id/copy"
        element={
          <FeatureFlag featureKey="workflow-agent">
            <WorkFlowAgentForm backHref={backHref} />
          </FeatureFlag>
        }
      />
      <Route
        path="/*"
        element={
          <Flex
            className={AppPagePadding}
            w="full"
            h="full"
            flexDirection="column"
            justifyContent="flex-start"
            gap="24"
          >
            <PageHeader
              title="Agents"
              headingText={
                <>
                  Empower your marketing team by customizing Opal agents for specific tasks and
                  workflows.{' '}
                  <Link
                    external
                    href="https://support.optimizely.com/hc/en-us/sections/39333461616141-Agents"
                  >
                    Learn more.
                  </Link>
                </>
              }
            />
            <Flex w="full">
              <Tabs onValueChange={handleTabChange} value={getActiveTab()}>
                <TabsList>
                  <TabLink path="/agents" value="agents" exactMatch>
                    Your Agents
                  </TabLink>
                  <FeatureFlag featureKey="workflow-agent">
                    <TabLink path="/agents/executions" value="executions">
                      Workflow Logs
                    </TabLink>
                  </FeatureFlag>
                  <TabLink path="/agents/agent-directory" value="agent-directory">
                    Agent Directory
                  </TabLink>
                </TabsList>

                <TabsContent py="16" value="agents">
                  <Routes>
                    <Route path="/" element={<AgentTab />} />
                  </Routes>
                </TabsContent>

                <TabsContent py="16" value="executions">
                  <FeatureFlag featureKey="workflow-agent">
                    <Routes>
                      <Route path="/executions" element={<AgentExecutionsTab />} />
                      <Route path="/executions/:executionId" element={<AgentExecutionsTab />} />
                    </Routes>
                  </FeatureFlag>
                </TabsContent>

                {/* TODO: Check if something needs to be changed here */}
                <TabsContent py="16" value="agent-directory">
                  <Routes>
                    <Route path="/agent-directory" element={<MarketPlaceTab />} />
                  </Routes>
                </TabsContent>
              </Tabs>
            </Flex>
          </Flex>
        }
      />
    </Routes>
  );
}
